import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

const MIN_AUTHOR_PX = 360;
const MIN_PREVIEW_PX = 360;
const SPLITTER_PX = 6;
const DEFAULT_AUTHOR_PX = 520;
const STORAGE_KEY = "ccs-lesson-workspace-split";

export interface WorkspaceSplit {
  readonly wideLayout: boolean;
  readonly dragging: boolean;
  readonly gridTemplateColumns: string | undefined;
  readonly onSplitterPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
}

function loadStored(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function persist(width: number) {
  try {
    localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    /* ignore quota errors */
  }
}

function clampAuthor(width: number, totalWidth: number): number {
  const max = totalWidth - SPLITTER_PX - MIN_PREVIEW_PX;
  return Math.max(MIN_AUTHOR_PX, Math.min(width, Math.max(MIN_AUTHOR_PX, max)));
}

/** Resizable two-pane (author | preview) split for the lesson workspace. */
export function useWorkspaceSplit(panelRef: RefObject<HTMLElement | null>): WorkspaceSplit {
  const [authorWidth, setAuthorWidth] = useState(() => loadStored() ?? DEFAULT_AUTHOR_PX);
  const [dragging, setDragging] = useState(false);
  const [wideLayout, setWideLayout] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : false
  );

  const widthRef = useRef(authorWidth);
  widthRef.current = authorWidth;
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setWideLayout(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [dragging]);

  const finishDrag = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    setDragging(false);
    if (drag) persist(widthRef.current);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      const panel = panelRef.current;
      if (!drag || !panel) return;
      const totalWidth = panel.getBoundingClientRect().width;
      const next = clampAuthor(drag.startWidth + (event.clientX - drag.startX), totalWidth);
      widthRef.current = next;
      setAuthorWidth(next);
    };
    const onUp = () => finishDrag();
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, finishDrag, panelRef]);

  useEffect(() => {
    if (!wideLayout) return;
    const panel = panelRef.current;
    if (!panel) return;
    const clampToPanel = () => {
      const totalWidth = panel.getBoundingClientRect().width;
      if (totalWidth <= 0) return;
      const next = clampAuthor(widthRef.current, totalWidth);
      widthRef.current = next;
      setAuthorWidth(next);
    };
    clampToPanel();
    const ro = new ResizeObserver(clampToPanel);
    ro.observe(panel);
    return () => ro.disconnect();
  }, [panelRef, wideLayout]);

  const onSplitterPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!wideLayout) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = { startX: event.clientX, startWidth: widthRef.current };
      setDragging(true);
    },
    [wideLayout]
  );

  const gridTemplateColumns = wideLayout
    ? `${authorWidth}px ${SPLITTER_PX}px minmax(${MIN_PREVIEW_PX}px, 1fr)`
    : undefined;

  return { wideLayout, dragging, gridTemplateColumns, onSplitterPointerDown };
}
